/**
 * Email Rate Limiting Service
 * Resolves CWE-770: Allocation of Resources Without Limits or Throttling
 * Prevents mail bombing and email abuse attacks
 */

class EmailRateLimitingService {
  constructor() {
    // Rate limiting configuration
    this.limits = {
      // Per user limits
      maxEmailsPerMinute: 5,
      maxEmailsPerHour: 30,
      maxEmailsPerDay: 100,
      
      // Per recipient limits
      maxRecipientsPerEmail: 10,
      maxUniqueRecipientsPerDay: 50,
      
      // Content-based limits
      maxEmailSize: 1024 * 1024, // 1MB
      maxSubjectLength: 200,
      maxBodyLength: 10000,
      
      // Attachment limits
      maxAttachmentsPerEmail: 5,
      maxAttachmentSize: 5 * 1024 * 1024, // 5MB
      
      // Frequency limits
      minIntervalBetweenEmails: 10000, // 10 seconds
      maxBulkEmailsPerHour: 5,
      
      // Abuse detection
      maxIdenticalEmails: 3,
      maxBouncesBeforeBlock: 5
    };

    // Tracking storage
    this.tracking = {
      userEmailCounts: new Map(), // userId -> { minute: [], hour: [], day: [] }
      recipientCounts: new Map(),  // userId -> { recipients: Set, daily: Set }
      lastEmailTime: new Map(),    // userId -> timestamp
      emailHashes: new Map(),      // userId -> [hash1, hash2, ...]
      bounces: new Map(),          // userId -> count
      blockedUsers: new Set(),     // Set of blocked user IDs
      suspiciousActivity: new Map() // userId -> { attempts: number, lastAttempt: timestamp }
    };

    // Start cleanup intervals
    this.startCleanupIntervals();
  }

  /**
   * Check if user can send email
   * @param {string} userId - User identifier
   * @param {Object} emailData - Email data to validate
   * @returns {Object} - Validation result
   */
  async canSendEmail(userId, emailData) {
    const result = {
      canSend: false,
      errors: [],
      warnings: [],
      remainingQuota: {},
      nextAllowedTime: null
    };

    try {
      // Check if user is blocked
      if (this.isUserBlocked(userId)) {
        result.errors.push('Account temporarily blocked due to abuse');
        return result;
      }

      // 1. Validate email structure
      const structureValidation = this.validateEmailStructure(emailData);
      if (!structureValidation.isValid) {
        result.errors.push(...structureValidation.errors);
        this.trackSuspiciousActivity(userId, 'invalid_structure');
      }

      // 2. Check rate limits
      const rateLimitValidation = this.checkRateLimits(userId);
      if (!rateLimitValidation.canSend) {
        result.errors.push(...rateLimitValidation.errors);
        result.nextAllowedTime = rateLimitValidation.nextAllowedTime;
      }

      // 3. Check recipient limits
      const recipientValidation = this.validateRecipients(userId, emailData.recipients);
      if (!recipientValidation.isValid) {
        result.errors.push(...recipientValidation.errors);
      }

      // 4. Check content limits
      const contentValidation = this.validateEmailContent(emailData);
      if (!contentValidation.isValid) {
        result.errors.push(...contentValidation.errors);
      }

      // 5. Check for duplicate/spam content
      const spamValidation = this.checkSpamPatterns(userId, emailData);
      if (!spamValidation.isValid) {
        result.errors.push(...spamValidation.errors);
        result.warnings.push(...spamValidation.warnings);
      }

      // 6. Check frequency limits
      const frequencyValidation = this.checkEmailFrequency(userId);
      if (!frequencyValidation.canSend) {
        result.errors.push(...frequencyValidation.errors);
        result.nextAllowedTime = frequencyValidation.nextAllowedTime;
      }

      // Calculate remaining quotas
      result.remainingQuota = this.calculateRemainingQuotas(userId);

      // Final decision
      result.canSend = result.errors.length === 0;

      return result;

    } catch (error) {
      result.errors.push(`Email validation failed: ${error.message}`);
      return result;
    }
  }

  /**
   * Track successful email send
   * @param {string} userId - User identifier
   * @param {Object} emailData - Email data that was sent
   */
  trackEmailSent(userId, emailData) {
    const now = Date.now();
    
    try {
      // Update send counts
      this.updateSendCounts(userId, now);
      
      // Track recipients
      this.trackRecipients(userId, emailData.recipients);
      
      // Update last send time
      this.tracking.lastEmailTime.set(userId, now);
      
      // Track email content hash for duplicate detection
      const contentHash = this.generateContentHash(emailData);
      this.trackContentHash(userId, contentHash);
      
      // Log successful send
      this.logEmailEvent('email_sent', {
        userId: userId,
        recipientCount: emailData.recipients?.length || 0,
        timestamp: now,
        contentHash: contentHash
      });

    } catch (error) {
      console.error('[EmailRateLimit] Failed to track email send:', error);
    }
  }

  /**
   * Validate email structure and basic parameters
   * @param {Object} emailData - Email data to validate
   * @returns {Object} - Validation result
   */
  validateEmailStructure(emailData) {
    const result = { isValid: true, errors: [] };

    // Check required fields
    if (!emailData.recipients || !Array.isArray(emailData.recipients)) {
      result.errors.push('Recipients list is required');
      result.isValid = false;
    }

    if (!emailData.subject || typeof emailData.subject !== 'string') {
      result.errors.push('Email subject is required');
      result.isValid = false;
    }

    // Validate recipients
    if (emailData.recipients) {
      const invalidEmails = emailData.recipients.filter(email => 
        !this.isValidEmail(email)
      );
      if (invalidEmails.length > 0) {
        result.errors.push(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        result.isValid = false;
      }
    }

    return result;
  }

  /**
   * Check various rate limits for user
   * @param {string} userId - User identifier
   * @returns {Object} - Rate limit check result
   */
  checkRateLimits(userId) {
    const result = { canSend: true, errors: [], nextAllowedTime: null };
    const now = Date.now();

    const userCounts = this.tracking.userEmailCounts.get(userId) || {
      minute: [],
      hour: [],
      day: []
    };

    // Check per-minute limit
    const minuteCount = userCounts.minute.filter(time => 
      now - time < 60 * 1000
    ).length;
    
    if (minuteCount >= this.limits.maxEmailsPerMinute) {
      result.canSend = false;
      result.errors.push(`Rate limit exceeded: ${this.limits.maxEmailsPerMinute} emails per minute`);
      result.nextAllowedTime = now + (60 * 1000);
    }

    // Check per-hour limit
    const hourCount = userCounts.hour.filter(time => 
      now - time < 60 * 60 * 1000
    ).length;
    
    if (hourCount >= this.limits.maxEmailsPerHour) {
      result.canSend = false;
      result.errors.push(`Rate limit exceeded: ${this.limits.maxEmailsPerHour} emails per hour`);
      result.nextAllowedTime = now + (60 * 60 * 1000);
    }

    // Check per-day limit
    const dayCount = userCounts.day.filter(time => 
      now - time < 24 * 60 * 60 * 1000
    ).length;
    
    if (dayCount >= this.limits.maxEmailsPerDay) {
      result.canSend = false;
      result.errors.push(`Rate limit exceeded: ${this.limits.maxEmailsPerDay} emails per day`);
      result.nextAllowedTime = now + (24 * 60 * 60 * 1000);
    }

    return result;
  }

  /**
   * Validate recipients and check recipient-based limits
   * @param {string} userId - User identifier
   * @param {Array} recipients - List of email recipients
   * @returns {Object} - Validation result
   */
  validateRecipients(userId, recipients) {
    const result = { isValid: true, errors: [] };

    if (!recipients || !Array.isArray(recipients)) {
      result.errors.push('Recipients must be provided as an array');
      result.isValid = false;
      return result;
    }

    // Check max recipients per email
    if (recipients.length > this.limits.maxRecipientsPerEmail) {
      result.errors.push(`Too many recipients: ${recipients.length}. Maximum allowed: ${this.limits.maxRecipientsPerEmail}`);
      result.isValid = false;
    }

    // Check daily unique recipients limit
    const userRecipients = this.tracking.recipientCounts.get(userId) || { daily: new Set() };
    const newUniqueRecipients = recipients.filter(email => 
      !userRecipients.daily.has(email.toLowerCase())
    );

    if (userRecipients.daily.size + newUniqueRecipients.length > this.limits.maxUniqueRecipientsPerDay) {
      result.errors.push(`Daily recipient limit exceeded: ${this.limits.maxUniqueRecipientsPerDay} unique recipients per day`);
      result.isValid = false;
    }

    // Validate email formats
    const invalidEmails = recipients.filter(email => !this.isValidEmail(email));
    if (invalidEmails.length > 0) {
      result.errors.push(`Invalid email formats: ${invalidEmails.slice(0, 3).join(', ')}${invalidEmails.length > 3 ? '...' : ''}`);
      result.isValid = false;
    }

    // Check for suspicious recipient patterns
    const suspiciousPatterns = this.detectSuspiciousRecipients(recipients);
    if (suspiciousPatterns.length > 0) {
      result.errors.push(`Suspicious recipient patterns detected: ${suspiciousPatterns.join(', ')}`);
      result.isValid = false;
    }

    return result;
  }

  /**
   * Validate email content limits
   * @param {Object} emailData - Email data
   * @returns {Object} - Validation result
   */
  validateEmailContent(emailData) {
    const result = { isValid: true, errors: [] };

    // Check subject length
    if (emailData.subject && emailData.subject.length > this.limits.maxSubjectLength) {
      result.errors.push(`Subject too long: ${emailData.subject.length} characters. Maximum: ${this.limits.maxSubjectLength}`);
      result.isValid = false;
    }

    // Check body length
    if (emailData.body && emailData.body.length > this.limits.maxBodyLength) {
      result.errors.push(`Email body too long: ${emailData.body.length} characters. Maximum: ${this.limits.maxBodyLength}`);
      result.isValid = false;
    }

    // Check attachments
    if (emailData.attachments) {
      if (emailData.attachments.length > this.limits.maxAttachmentsPerEmail) {
        result.errors.push(`Too many attachments: ${emailData.attachments.length}. Maximum: ${this.limits.maxAttachmentsPerEmail}`);
        result.isValid = false;
      }

      const oversizedAttachments = emailData.attachments.filter(att => 
        att.size > this.limits.maxAttachmentSize
      );
      if (oversizedAttachments.length > 0) {
        result.errors.push(`Attachment(s) too large. Maximum size: ${this.limits.maxAttachmentSize / (1024 * 1024)}MB`);
        result.isValid = false;
      }
    }

    return result;
  }

  /**
   * Check for spam patterns and duplicate content
   * @param {string} userId - User identifier
   * @param {Object} emailData - Email data
   * @returns {Object} - Validation result
   */
  checkSpamPatterns(userId, emailData) {
    const result = { isValid: true, errors: [], warnings: [] };

    // Generate content hash
    const contentHash = this.generateContentHash(emailData);
    const userHashes = this.tracking.emailHashes.get(userId) || [];

    // Check for identical emails
    const identicalCount = userHashes.filter(hash => hash === contentHash).length;
    if (identicalCount >= this.limits.maxIdenticalEmails) {
      result.errors.push(`Identical email sent too many times: ${identicalCount}/${this.limits.maxIdenticalEmails}`);
      result.isValid = false;
    }

    // Check for spam keywords in subject/body
    const spamKeywords = ['urgent', 'winner', 'prize', 'click here', 'act now', 'limited time'];
    const content = `${emailData.subject || ''} ${emailData.body || ''}`.toLowerCase();
    const spamMatches = spamKeywords.filter(keyword => content.includes(keyword));
    
    if (spamMatches.length > 2) {
      result.warnings.push(`Email contains potential spam keywords: ${spamMatches.join(', ')}`);
    }

    // Check for excessive capitalization
    if (emailData.subject && this.isExcessivelyCapitalized(emailData.subject)) {
      result.warnings.push('Subject contains excessive capitalization');
    }

    return result;
  }

  /**
   * Check email sending frequency
   * @param {string} userId - User identifier
   * @returns {Object} - Frequency check result
   */
  checkEmailFrequency(userId) {
    const result = { canSend: true, errors: [], nextAllowedTime: null };
    const now = Date.now();
    const lastEmailTime = this.tracking.lastEmailTime.get(userId);

    if (lastEmailTime) {
      const timeSinceLastEmail = now - lastEmailTime;
      if (timeSinceLastEmail < this.limits.minIntervalBetweenEmails) {
        result.canSend = false;
        const waitTime = this.limits.minIntervalBetweenEmails - timeSinceLastEmail;
        result.errors.push(`Please wait ${Math.ceil(waitTime / 1000)} seconds before sending another email`);
        result.nextAllowedTime = lastEmailTime + this.limits.minIntervalBetweenEmails;
      }
    }

    return result;
  }

  /**
   * Check if user is blocked
   * @param {string} userId - User identifier
   * @returns {boolean} - True if user is blocked
   */
  isUserBlocked(userId) {
    return this.tracking.blockedUsers.has(userId);
  }

  /**
   * Block user temporarily
   * @param {string} userId - User identifier
   * @param {string} reason - Reason for blocking
   * @param {number} duration - Block duration in milliseconds
   */
  blockUser(userId, reason, duration = 60 * 60 * 1000) { // Default 1 hour
    this.tracking.blockedUsers.add(userId);
    
    console.warn(`[EmailRateLimit] User blocked: ${userId} - ${reason}`);
    
    // Auto-unblock after duration
    setTimeout(() => {
      this.tracking.blockedUsers.delete(userId);
      console.info(`[EmailRateLimit] User unblocked: ${userId}`);
    }, duration);
    
    this.logEmailEvent('user_blocked', {
      userId: userId,
      reason: reason,
      duration: duration,
      timestamp: Date.now()
    });
  }

  /**
   * Track suspicious activity
   * @param {string} userId - User identifier
   * @param {string} activity - Type of suspicious activity
   */
  trackSuspiciousActivity(userId, activity) {
    const now = Date.now();
    const userActivity = this.tracking.suspiciousActivity.get(userId) || {
      attempts: 0,
      lastAttempt: 0,
      activities: []
    };

    userActivity.attempts++;
    userActivity.lastAttempt = now;
    userActivity.activities.push({ type: activity, timestamp: now });

    // Keep only recent activities (last hour)
    userActivity.activities = userActivity.activities.filter(act => 
      now - act.timestamp < 60 * 60 * 1000
    );

    this.tracking.suspiciousActivity.set(userId, userActivity);

    // Block user if too many suspicious attempts
    if (userActivity.attempts >= 10) {
      this.blockUser(userId, `Excessive suspicious activity: ${activity}`, 2 * 60 * 60 * 1000); // 2 hours
    }
  }

  /**
   * Update send counts for user
   * @param {string} userId - User identifier
   * @param {number} timestamp - Send timestamp
   */
  updateSendCounts(userId, timestamp) {
    const userCounts = this.tracking.userEmailCounts.get(userId) || {
      minute: [],
      hour: [],
      day: []
    };

    userCounts.minute.push(timestamp);
    userCounts.hour.push(timestamp);
    userCounts.day.push(timestamp);

    this.tracking.userEmailCounts.set(userId, userCounts);
  }

  /**
   * Track recipients for user
   * @param {string} userId - User identifier
   * @param {Array} recipients - List of recipients
   */
  trackRecipients(userId, recipients) {
    const userRecipients = this.tracking.recipientCounts.get(userId) || {
      recipients: new Set(),
      daily: new Set()
    };

    recipients.forEach(email => {
      userRecipients.recipients.add(email.toLowerCase());
      userRecipients.daily.add(email.toLowerCase());
    });

    this.tracking.recipientCounts.set(userId, userRecipients);
  }

  /**
   * Generate content hash for duplicate detection
   * @param {Object} emailData - Email data
   * @returns {string} - Content hash
   */
  generateContentHash(emailData) {
    const content = `${emailData.subject || ''}|${emailData.body || ''}|${(emailData.recipients || []).sort().join(',')}`;
    
    // Simple hash function (use crypto.subtle.digest in production)
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return hash.toString();
  }

  /**
   * Track content hash for user
   * @param {string} userId - User identifier
   * @param {string} contentHash - Content hash
   */
  trackContentHash(userId, contentHash) {
    const userHashes = this.tracking.emailHashes.get(userId) || [];
    userHashes.push(contentHash);
    
    // Keep only last 50 hashes
    if (userHashes.length > 50) {
      userHashes.splice(0, userHashes.length - 50);
    }
    
    this.tracking.emailHashes.set(userId, userHashes);
  }

  /**
   * Calculate remaining quotas for user
   * @param {string} userId - User identifier
   * @returns {Object} - Remaining quotas
   */
  calculateRemainingQuotas(userId) {
    const now = Date.now();
    const userCounts = this.tracking.userEmailCounts.get(userId) || {
      minute: [], hour: [], day: []
    };

    const minuteCount = userCounts.minute.filter(time => now - time < 60 * 1000).length;
    const hourCount = userCounts.hour.filter(time => now - time < 60 * 60 * 1000).length;
    const dayCount = userCounts.day.filter(time => now - time < 24 * 60 * 60 * 1000).length;

    const userRecipients = this.tracking.recipientCounts.get(userId) || { daily: new Set() };

    return {
      emailsPerMinute: Math.max(0, this.limits.maxEmailsPerMinute - minuteCount),
      emailsPerHour: Math.max(0, this.limits.maxEmailsPerHour - hourCount),
      emailsPerDay: Math.max(0, this.limits.maxEmailsPerDay - dayCount),
      uniqueRecipientsPerDay: Math.max(0, this.limits.maxUniqueRecipientsPerDay - userRecipients.daily.size)
    };
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - True if valid
   */
  isValidEmail(email) {
    if (!email || email.length > 254) return false;
    const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;
    return emailRegex.test(email);
  }

  /**
   * Detect suspicious recipient patterns
   * @param {Array} recipients - List of recipients
   * @returns {Array} - List of detected patterns
   */
  detectSuspiciousRecipients(recipients) {
    const patterns = [];

    // Check for sequential emails (user1@domain.com, user2@domain.com, etc.)
    const domains = recipients.map(email => email.split('@')[1]);
    const domainCounts = {};
    domains.forEach(domain => {
      domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    });

    // Flag if more than 70% of recipients are from same domain (only for large recipient lists)
    const maxDomainCount = Math.max(...Object.values(domainCounts));
    if (maxDomainCount / recipients.length > 0.7 && recipients.length > 15) {
      patterns.push('bulk_same_domain');
    }

    return patterns;
  }

  /**
   * Check if text is excessively capitalized
   * @param {string} text - Text to check
   * @returns {boolean} - True if excessive
   */
  isExcessivelyCapitalized(text) {
    if (text.length < 10) return false;
    const upperCount = (text.match(/[A-Z]/g) || []).length;
    return upperCount / text.length > 0.5;
  }

  /**
   * Start cleanup intervals
   */
  startCleanupIntervals() {
    // Clean up old data every 5 minutes
    setInterval(() => {
      this.cleanupOldData();
    }, 5 * 60 * 1000);
  }

  /**
   * Clean up old tracking data
   */
  cleanupOldData() {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    // Clean up email counts
    for (const [userId, counts] of this.tracking.userEmailCounts.entries()) {
      counts.minute = counts.minute.filter(time => now - time < 60 * 1000);
      counts.hour = counts.hour.filter(time => now - time < 60 * 60 * 1000);
      counts.day = counts.day.filter(time => now - time < dayMs);
      
      if (counts.minute.length === 0 && counts.hour.length === 0 && counts.day.length === 0) {
        this.tracking.userEmailCounts.delete(userId);
      }
    }

    // Clean up daily recipient counts
    for (const [userId, recipients] of this.tracking.recipientCounts.entries()) {
      recipients.daily.clear(); // Reset daily count
    }

    // Clean up suspicious activity older than 1 day
    for (const [userId, activity] of this.tracking.suspiciousActivity.entries()) {
      if (now - activity.lastAttempt > dayMs) {
        this.tracking.suspiciousActivity.delete(userId);
      }
    }
  }

  /**
   * Log email events for monitoring
   * @param {string} event - Event type
   * @param {Object} details - Event details
   */
  logEmailEvent(event, details) {
    const logEntry = {
      event: event,
      details: details,
      timestamp: new Date().toISOString()
    };



    // Send to monitoring system if available
    // this.sendToMonitoring(logEntry);
  }

  /**
   * Get current rate limiting status
   * @param {string} userId - User identifier
   * @returns {Object} - Current status and limits
   */
  getStatus(userId) {
    return {
      isBlocked: this.isUserBlocked(userId),
      remainingQuotas: this.calculateRemainingQuotas(userId),
      limits: this.limits,
      lastEmailTime: this.tracking.lastEmailTime.get(userId),
      suspiciousActivity: this.tracking.suspiciousActivity.get(userId)
    };
  }

  /**
   * Update rate limiting configuration
   * @param {Object} newLimits - New limit values
   */
  updateLimits(newLimits) {
    this.limits = { ...this.limits, ...newLimits };
    console.info('[EmailRateLimit] Limits updated:', this.limits);
  }
}

// Export singleton instance
export const emailRateLimitingService = new EmailRateLimitingService();
export default emailRateLimitingService;